<template>
  <article class="bookmark-item">
    <component
      :is="comp"
      :bookmark="bookmark" :user-id="userId" :membership="membership" :public-mode="publicMode"
      @toggle-select="onToggleSelect"
      @tag-selected="onSelectTag"
      @favorite="onAction('favorite', $event)"
      @save="onAction('save', $event)"
      @comments="onAction('comments', $event)"
      @add-to-collection="onAction('add-to-collection', $event)"
      @add-to-collection-from-collection="onAction('add-to-collection-from-collection', $event)"
      @like="onAction('like', $event)"
      @likes="onAction('likes', $event)"
      @pin-edit="onAction('pin-edit', $event)"
      @more="onMoreOptions"
    />

    <!-- Options menu: shown when not in public mode, OR when in public mode and user has some actions -->
    <BookmarkOptions
      v-if="dirty && showOptions"
      v-model="optionsOpen"
      :bookmark="menuBookmark || bookmark"
      :user-id="userId"
      :membership="membership"
      :public-mode="publicMode"
      :with-secondary-action="false"
      :with-primary-action="false"
      :is-grid-layout="isGridLayout"
      @comments="onAction('comments', $event)"
      @save="onAction('save', $event)"
      @add-to-collection="onAction('add-to-collection', $event)"
      @tags="onAction('tags', $event)"
      @edit="onAction('edit', $event)"
      @delete="onAction('delete', $event)"
      @share="onAction('share', $event)"
      @pin="onAction('pin', $event)"
      @unpin="onAction('unpin', $event)"
    />

    <BookmarkTagsFilter
      v-if="dirty"
      v-model="tagsFilterOpen"
      :activator="$el" :bookmark="menuBookmark || bookmark" :user-id="userId"
      :membership="membership"
      :public-mode="publicMode"
      @submit="$emit('filter-tags', $event)"
      @edit="onAction('tags', $event)"
    />
  </article>
</template>

<script setup>
import { ref, computed, watch, defineProps, defineEmits } from 'vue'
import { useDisplay } from 'vuetify'
import { ITEM_LAYOUT_STYLE } from '@/utils/constants'
import BookmarkCard from './BookmarkCard.vue'
import BookmarkListItem from './BookmarkListItem.vue'
import BookmarkHeadline from './BookmarkHeadline.vue'
import BookmarkTile from './BookmarkTile.vue'
import BookmarkOptions from './BookmarkOptions.vue'
import BookmarkTagsFilter from './BookmarkTagsFilter.vue'

const props = defineProps({
  // Accepts both Bookmark and CollectionItem (when resourceType is 'bookmark')
  bookmark: {
    type: Object,
    required: true,
  },
  userId: {
    type: String,
    default: null,
  },
  membership: {
    type: Object,
    default: null,
  },
  listStyle: {
    type: String,
    default: 'card',
  },
  publicMode: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['action', 'filter-tags'])

const STYLES = {
  [ITEM_LAYOUT_STYLE.cards]: BookmarkCard,
  [ITEM_LAYOUT_STYLE.list]: BookmarkListItem,
  [ITEM_LAYOUT_STYLE.headlines]: BookmarkHeadline,
  [ITEM_LAYOUT_STYLE.tiles]: BookmarkTile,
}

const dirty = ref(false)
const tagsFilterOpen = ref(false)
const optionsOpen = ref(false)
// Snapshot of the bookmark at the moment a menu/sheet was opened. Decouples
// the displayed content from props.bookmark so iOS Safari row-recycling
// during the touchend→click window can't show the wrong title or fast-close.
const menuBookmark = ref(null)
const display = useDisplay()

// Reset menu state when the row is recycled by vue-virtual-scroller —
// without this, stale local state leaks to a different bookmark.
// Skip while a menu/sheet is open: the UI is bound to the snapshot, not props.
watch(() => props.bookmark?._id, (newId, oldId) => {
  if (newId === oldId) return
  if (optionsOpen.value || tagsFilterOpen.value) return
  dirty.value = false
  menuBookmark.value = null
})

watch([optionsOpen, tagsFilterOpen], ([opts, tags]) => {
  if (!opts && !tags) menuBookmark.value = null
})


const comp = computed(() => {
  return STYLES[props.listStyle]
})

const isGridLayout = computed(() => {
  return [ITEM_LAYOUT_STYLE.cards, ITEM_LAYOUT_STYLE.tiles].includes(props.listStyle) && display.smAndUp.value
})

const showOptions = computed(() => {
  return true
})

function onSelectTag() {
  menuBookmark.value = props.bookmark
  dirty.value = true
  tagsFilterOpen.value = true
}

function onMoreOptions() {
  menuBookmark.value = props.bookmark
  dirty.value = true
  optionsOpen.value = true
}

function onAction(action, event) {
  emit('action', { action, event })
}

function onToggleSelect(event) {
  emit('action', { action: 'toggle-select', event })
}
</script>
